import { lazy, useEffect, useMemo, useState } from 'react'
import { entreesFlotte } from '../../../../../../web-commun/historique-gps/entrees'
import { EcranHistorique } from '../../../../../../web-commun/historique-gps/EcranHistorique'
import { sourceReelle, type SourceHistorique } from '../../../../../../web-commun/historique-gps/api'
import type { EntreeFlotte } from '../../../../../../web-commun/historique-gps/types'
import { supabase } from '../../api/supabase'
import type { DonneesOps } from '../../api/types'
import { useDroits } from '../Contexte'

/**
 * « Historique GPS » de la console opérateur — trajets passés, rejeu et rapports de NOTRE flotte (migration 0057). L'écran est celui du
 * portail SaaS (`web-commun/historique-gps`) ; ce fichier branche cette console : sa flotte, son client de données, son fond de carte.
 *
 * L'opératrice est exemptée de palier (palier effectif « business » : 180 jours conservés, alertes disponibles). Équipe seulement
 * (propriétaire, dispatcher, comptable) : la base refuse tout autre rôle.
 */
const CarteHistorique = lazy(() => import('../CarteHistorique'))

export function EcranHistoriqueGps({ donnees, orgId, surAllerAlertes }: { donnees: DonneesOps; orgId: string; surAllerAlertes: (() => void) | undefined }) {
  const { apercu } = useDroits()
  const [source, setSource] = useState<SourceHistorique | null>(null)
  const [flotteApercu, setFlotteApercu] = useState<readonly EntreeFlotte[] | null>(null)

  useEffect(() => {
    let vivant = true
    // Aperçu de développement : positions fictives dans Bamako, aucune base. Le test littéral `import.meta.env.DEV` fait disparaître
    // ce bloc (et le module d'aperçu) du paquet de production.
    if (import.meta.env.DEV && apercu) {
      void import('../../../../../../web-commun/historique-gps/apercu').then((m) => {
        if (!vivant) return
        setFlotteApercu(m.FLOTTE_APERCU)
        setSource(m.sourceApercu('business'))
      })
    } else {
      setSource(sourceReelle(supabase, orgId))
    }
    return () => {
      vivant = false
    }
  }, [apercu, orgId])

  const flotte = useMemo<readonly EntreeFlotte[]>(
    () =>
      flotteApercu ??
      entreesFlotte(
        donnees.boitiers.map((b) => ({ id: b.id, uniqueId: b.unique_id, label: b.label, vehicleId: b.vehicle_id, driverId: b.driver_id, active: b.active })),
        donnees.vehicules.map((v) => ({ id: v.id, plate: v.plate, model: v.model })),
        donnees.drivers.map((d) => ({ id: d.id, nom: d.full_name })),
      ),
    [flotteApercu, donnees.boitiers, donnees.vehicules, donnees.drivers],
  )

  if (import.meta.env.DEV && apercu && !flotteApercu) return <p style={{ padding: 18, color: 'var(--muted)' }}>Préparation de l’aperçu…</p>

  return (
    <EcranHistorique
      periodeInitiale={import.meta.env.DEV && apercu ? 'hier' : undefined}
      source={source}
      flotte={flotte}
      Carte={CarteHistorique}
      nomOrganisation={donnees.org.name}
      surOuvrirAlertes={surAllerAlertes}
    />
  )
}
